import { useMemo } from 'react';
import { Anchor, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { handlingUnitsStepMessages } from './handling-units-step-messages';
import { WizardCommodityCard } from './WizardCommodityCard';
import { PACKAGING_TYPE_OPTIONS } from '../../../../ltl/constants';
import { CustomNumberInput, DimensionsInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { createEmptyCommodity } from '../../create-empty-handling-unit';

import type { Dimensions } from '../../../../types/dimensions';
import type { FreightPackagingType, LtlCommodity, LtlHandlingUnit } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';

export type WizardHandlingUnitCardProps = {
  unit: LtlHandlingUnit;
  index: number;
  displayWeightUnit: WeightUnit;
  onChange: (next: LtlHandlingUnit) => void;
  onRemove?: () => void;
  productSuggestions?: string[];
  onProductSearch?: (query: string) => void;
  onProductSelect?: (commodityId: string, productName: string) => void;
};

export const WizardHandlingUnitCard = ({
  unit,
  index,
  displayWeightUnit,
  onChange,
  onRemove,
  productSuggestions,
  onProductSearch,
  onProductSelect,
}: WizardHandlingUnitCardProps) => {
  const intl = useIntl();

  const packagingTypeData = useMemo(
    () => PACKAGING_TYPE_OPTIONS.map((option) => ({ value: option.value, label: intl.formatMessage(option.label) })),
    [intl],
  );

  const dimensionsValue: Dimensions = {
    length: unit.length,
    width: unit.width,
    height: unit.height,
    unit: (unit.dimensionUnit as Dimensions['unit']) ?? 'Inches',
  };

  const handleQuantityChange = (value: number | string) => {
    const parsed = typeof value === 'string' ? parseInt(value, 10) || 1 : value;
    onChange({ ...unit, quantity: parsed });
  };

  const handleTypeChange = (value: string | null) => {
    if (value) onChange({ ...unit, packagingType: value as FreightPackagingType });
  };

  const handleStackableChange = (value: string | null) => onChange({ ...unit, isStackable: value === 'yes' });

  const handleDimensionsChange = (next: Dimensions) => onChange({ ...unit, length: next.length, width: next.width, height: next.height });

  const handleCommodityChange = (commodityIndex: number, updated: LtlCommodity) => {
    const commodities = [...unit.commodities];
    commodities[commodityIndex] = updated;
    onChange({ ...unit, commodities });
  };

  const handleAddCommodity = () =>
    onChange({ ...unit, commodities: [...unit.commodities, createEmptyCommodity(unit.commodities, displayWeightUnit)] });

  const handleRemoveCommodity = (commodityIndex: number) =>
    onChange({ ...unit, commodities: unit.commodities.filter((_, idx) => idx !== commodityIndex) });

  return (
    <Paper data-testid={`handling-unit-card-${index}`} p='md' shadow='none' withBorder>
      <Stack gap='sm'>
        <Group justify='space-between'>
          <Text fw={600}>{intl.formatMessage(handlingUnitsStepMessages.handlingUnitTitle, { number: index + 1 })}</Text>
          {onRemove ? (
            <Button color='red' data-testid={`remove-unit-${index}`} onClick={onRemove} size='xs' variant='ghost'>
              {intl.formatMessage(handlingUnitsStepMessages.removeHandlingUnit)}
            </Button>
          ) : null}
        </Group>

        <Group grow>
          <CustomNumberInput
            allowNegative={false}
            data-testid={`unit-quantity-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.quantityLabel)}
            min={1}
            onChange={handleQuantityChange}
            value={unit.quantity}
          />
          <Dropdown
            data={packagingTypeData}
            data-testid={`unit-type-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.unitTypeLabel)}
            onChange={handleTypeChange}
            value={unit.packagingType}
          />
          <Dropdown
            data={[
              { value: 'yes', label: intl.formatMessage(handlingUnitsStepMessages.stackableYes) },
              { value: 'no', label: intl.formatMessage(handlingUnitsStepMessages.stackableNo) },
            ]}
            data-testid={`unit-stackable-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.stackableLabel)}
            onChange={handleStackableChange}
            value={unit.isStackable ? 'yes' : 'no'}
          />
        </Group>

        <Text size='sm'>{intl.formatMessage(freightFormMessages.dimensionsLabel, { unit: dimensionsValue.unit })}</Text>
        <DimensionsInput onChange={handleDimensionsChange} value={dimensionsValue} />

        {unit.commodities.map((commodity, cIdx) => (
          <WizardCommodityCard
            commodity={commodity}
            dimensions={dimensionsValue}
            displayWeightUnit={displayWeightUnit}
            index={cIdx}
            key={commodity.id}
            onChange={(updated) => handleCommodityChange(cIdx, updated)}
            onProductSearch={onProductSearch}
            onProductSelect={onProductSelect}
            onRemove={cIdx > 0 ? () => handleRemoveCommodity(cIdx) : undefined}
            productSuggestions={productSuggestions}
          />
        ))}

        <Anchor component='button' data-testid={`add-commodity-${index}`} onClick={handleAddCommodity} size='sm' type='button'>
          {intl.formatMessage(handlingUnitsStepMessages.addCommodity)}
        </Anchor>
      </Stack>
    </Paper>
  );
};
